import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GlobalLoadingService } from '../../global-services/global-loading.service';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { getAuth, sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { Router } from '@angular/router';
import { MeService } from '../../global-services/me.service';
import { SigninService } from '../../global-services/signin.service';
import { FirebaseService } from '../../global-services/firebase.service';

export type SignInState = 'idle' | 'display' | 'displayWithErrors';

@Injectable({ providedIn: 'root' })
export class SignInFacade {
  form: FormGroup;
  private stateSubject = new BehaviorSubject<SignInState>('idle');
  state$ = this.stateSubject.asObservable();
  private currentInviteToken?: string | null = null;

  constructor(
    private fb: FormBuilder,
    private loading: GlobalLoadingService,
    private message: GlobalMessageService,
    private router: Router,
    private userService: MeService,
    private signinService: SigninService,
    private firebaseService: FirebaseService,
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
      rememberMe: [false],
    });
  }

  async start(email?: string | null, inviteToken?: string | null) {
    this.stateSubject.next('display');
    this.currentInviteToken = null;
    this.form.reset({ rememberMe: false });

    if (email) {
      this.form.patchValue({ email });
    }

    if (inviteToken) {
      this.currentInviteToken = inviteToken;
    }
  }

  canOpenUpLostPasswordDialog(): boolean {
    const validEmail = this.form.get('email')?.valid ?? false;

    if (!validEmail) {
      this.message.showError('Error', 'Please enter a valid email address to reset your password.');
    }

    return validEmail;
  }

  async sendPasswordResetEmail() {
    try {
      this.loading.show();
      const validEmail = this.form.get('email')?.value;
      const auth = getAuth();

      await sendPasswordResetEmail(auth, validEmail);

      this.message.showSuccess('Password Reset', 'Password reset email sent successfully!');
    } catch (error) {
      const errorObj = error as any;
      const message = errorObj.error?.message || errorObj.message || 'unknown error';
      this.message.showError('Reset Password Failed', message);
    } finally {
      this.loading.hide();
    }
  }

  async check2FA(code: string) {
    try {
      this.loading.show();
      const resp = await this.signinService.twoFactorCheck(this.form.value.email, this.form.value.password, code);

      //Sign in with custom token
      const firebaseToken = resp.token;
      const userCredential = await this.firebaseService.signInWithCustomToken(firebaseToken);

      await this.userService.refreshMe();

      if (this.currentInviteToken) {
        const inviteRoute = 'accept-invite/' + this.currentInviteToken;
        this.router.navigate([inviteRoute]);
        return;
      }

      this.router.navigate(['/home']);
      this.message.showSuccess('Welcome Back', 'Signed in successfully!');
    } catch (error) {
      const errorObj = error as any;
      const message = errorObj.error?.message || errorObj.message || 'unknown error';
      this.message.showError('Sign In Failed', message);
    } finally {
      this.loading.hide();
    }
  }

  async start2FA(): Promise<boolean> {
    try {
      this.loading.show();
      if (this.form.invalid) {
        this.form.markAllAsTouched();
        this.stateSubject.next('displayWithErrors');
        this.message.showError('Error', 'Please correct the errors in the form.');
        return false;
      }

      const resp = await this.signinService.twoFactorRequest(this.form.value.email, this.form.value.password);

      this.message.showSuccess('2FA Requested', 'A two-factor authentication token has been sent to your email.');
      return true;

      /*const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, this.form.value.email, this.form.value.password);
      await this.userService.refreshMe();

      if (this.currentInviteToken) {
        const inviteRoute = 'accept-invite/' + this.currentInviteToken;
        this.router.navigate([inviteRoute]);
        return;
      }

      this.router.navigate(['/home']);
      this.message.showSuccess('Welcome Back', 'Signed in successfully!');*/
    } catch (error) {
      const errorObj = error as any;
      const message = errorObj.error?.message || errorObj.message || 'unknown error';
      this.message.showError('Sign In Failed', message);
      return false;
    } finally {
      this.loading.hide();
    }
  }
}
